import { useEffect, useState } from "react";
import { useGetLead } from "hooks/useGetLead";
import "../AIAgents/scss/agentDialogs.scss";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, Menu, MenuItem, Select, Tab, Tabs, TextField } from "@mui/material";
import { FaArchive, FaEllipsisV, FaSave, FaTrash } from "react-icons/fa";
import { LeadMainInfoV2 } from "./components/LeadMainInfoV2";
import { LeadDocuments } from "./components/LeadDocuments";
import { Controller, FormProvider, useForm } from "react-hook-form";
import leadsService from "services/admin/leadsService";
import { toast } from "react-toastify";
import { Lead } from "services/models/Lead";
import { FaPencil } from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { queryClient } from "queryClient";
import { LeadTasks } from "./components/LeadTasks";
import { useGetPipelines } from "hooks/useGetPipelines";

const getStepColor = (step: any) => {
  switch (step.name) {
    case 'Новая заявка':
      return 'info'
    case 'В работе':
      return 'warning'
    case 'Оплата':
      return 'success'
    case 'Успешно реализовано':
      return 'success'
    case 'Отказ':
      return 'error'
    default:
  }
  return 'primary'
}

interface LeadFormProps {
  leadID: any
  lead: any | null
  onClose: (updatedLeadOrNewLead: { isNew: boolean, lead: Lead }) => void
  pipelines: any[]
  selectedPipelineID: string
}

const LeadForm = ({ leadID, lead, onClose, pipelines, selectedPipelineID }: LeadFormProps) => {
  const methods = useForm({
    defaultValues: {
      name: lead?.name || '',
      status: lead?.status || 'NEW',
      budget: lead?.budget || '',
      currency: lead?.currency || 'KZT',
      type: lead?.type,
      pipelineID: lead?.pipelineID || selectedPipelineID,
      stepID: lead?.stepID || _.get(pipelines.find((p: any) => p.id === selectedPipelineID), 'steps.0.id'),
      customers: lead?.customers || [],
    },
    mode: "onChange",
  })

  useEffect(() => {
    if (lead) {
      methods.reset({
        name: lead?.name || '',
        status: lead?.status || 'NEW',
        budget: lead?.budget || '',
        currency: lead?.currency || 'KZT',
        type: lead?.type,
        pipelineID: lead?.pipelineID || selectedPipelineID,
        stepID: lead?.stepID || _.get(pipelines.find((p: any) => p.id === selectedPipelineID), 'steps.0.id'),
        customers: lead?.customers || [],
      })
    }
  }, [lead, pipelines, selectedPipelineID])

  const [moreOptionsAnchorEl, setMoreOptionsAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTab, setSelectedTab] = useState('main');
  const [isOpenDeleteLead, setIsOpenDeleteLead] = useState(false);
  const [isEditName, setIsEditName] = useState(false);
  const pipelineID = methods.watch('pipelineID')
  const pipeline = pipelines?.find((p: any) => p.id === pipelineID)

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMoreOptionsAnchorEl(event.currentTarget);
  };


  const createOrUpdateLead = async (formData: any) => {
    try {
      if (leadID) {
        const { data: updatedLead } = await leadsService.put(leadID, formData);

        queryClient.setQueriesData({ queryKey: ['leads', { pipelineID }] }, (old: any) => {
          return {
            ...old,
            rows: old.rows.map((l: Lead) => l.id === leadID ? updatedLead : l),
          }
        })
        queryClient.invalidateQueries({ queryKey: ['events', leadID] })
        onClose({
          isNew: false,
          lead: updatedLead,
        });
      } else {
        const { data: newLead } = await leadsService.post(formData);
        queryClient.setQueriesData({ queryKey: ['leads', { pipelineID }] }, (old: any) => {
          return {
            ...old,
            rows: [...old.rows, newLead],
          }
        })

        onClose({
          isNew: true,
          lead: newLead,
        });
      }
    } catch (error) {
      console.error(error);
      toast.error("Ошибка при обновлении сделки!");
    }
  }

  const deleteCustomerFromLead = async (customerID: string) => {
    try {
      // await leadsService.deleteContactFromLead(leadID, contactID);
      // toast.success("Контакт удален из сделки успешно!");

      const oldCustomers = methods.getValues('customers') || []
      methods.setValue('customers', oldCustomers.filter((customer: any) => customer.id !== customerID), {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })

      // Update lead cache
      queryClient.setQueryData(['lead', leadID], (old: any) => {
        return {
          ...old,
          customers: old.customers.filter((customer: any) => customer.id !== customerID),
        }
      })
    } catch (error) {
      console.error(error);
      toast.error("Ошибка при удалении покупателя из сделки!");
    }
  }

  const addCustomerToLead = async (customer: any) => {
    try {
      const oldCustomers = methods.getValues('customers') || []
      methods.setValue('customers', [...oldCustomers, customer], {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      })

      // Update lead cache
      queryClient.setQueryData(['lead', leadID], (old: any) => {
        return {
          ...old,
          customers: [...oldCustomers, customer],
        }
      })
    } catch (error) {
      console.error(error);
      toast.error("Ошибка при добавлении покупателя в сделку!");
    }
  }

  const onDeleteLead = async () => {
    console.log("leadID:", leadID)
    setIsOpenDeleteLead(false)

    try {
      await leadsService.delete(leadID);

      queryClient.invalidateQueries({ queryKey: ['leads', { pipelineID }] })
      onClose({
        isNew: false,
        lead: null as any,
      });
    } catch (error) {
      toast.error("Ошибка при удалении сделки!");
      console.error(error)
    }
  }

  const archiveOrRestoreLead = async () => {
    const newStatus = lead?.status === 'ARCHIVED' ? 'NEW' : 'ARCHIVED'
    try {
      if (!leadID) {
        toast.error("Сделка не найдена!");
        return;
      }

      if (!window.confirm(`Вы уверены, что хотите ${newStatus === 'ARCHIVED' ? 'архивировать' : 'восстановить'} эту сделку?`)) {
        return;
      }

      await leadsService.put(leadID, { status: newStatus })
      queryClient.setQueriesData({ queryKey: ['leads', { pipelineID }] }, (old: any) => {
        return {
          ...old,
          rows: old.rows.map((l: Lead) => l.id === leadID ? { ...l, status: newStatus } : l),
        }
      })
      onClose({
        isNew: false,
        lead: null as any,
      });
      toast.success(`Сделка успешно ${newStatus === 'ARCHIVED' ? 'архивирована' : 'восстановлена'}!`);
    } catch (error) {
      toast.error(`Ошибка при ${newStatus === 'ARCHIVED' ? 'архивировании' : 'восстановлении'} сделки!`);
      console.error(error)
    }
  }

  return (
    <div>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(createOrUpdateLead)}>
          <section id="lead-detail">
            <div className="lead-header flex flex-space-between items-center">
              <div className="lead-title text-lg font-bold">
                {isEditName || !leadID ? (
                  <div>
                    <TextField
                      style={{ minWidth: '300px' }}
                      placeholder="Сделка #"
                      size="small" {...methods.register('name')} fullWidth />
                  </div>

                ) : (
                  <span onClick={() => setIsEditName(true)} className="cursor-pointer">
                    {lead?.name}
                    <IconButton onClick={() => setIsEditName(false)}>
                      <FaPencil size={12} />
                    </IconButton>
                  </span>
                )}
                <span className="text-sm text-gray-500 ml-2">#{lead?.id}</span>
              </div>
              <div className="lead-actions flex gap-2">
                <Button size="small" variant="outlined" color="primary" type="submit">
                  <FaSave size={12} className="mr-2" />
                  Сохранить
                </Button>
                <Button size="small" variant="outlined" color="info" onClick={archiveOrRestoreLead}>
                  <FaArchive size={12} className="mr-2" />
                  {lead?.status === 'ARCHIVED' ? 'Восстановить' : 'Архивировать'}
                </Button>
                <Button size="small" variant="outlined" color="info" onClick={handleOpenMenu}>
                  <FaEllipsisV size={12} />
                </Button>
                <Menu
                  anchorEl={moreOptionsAnchorEl}
                  open={Boolean(moreOptionsAnchorEl)}
                  onClose={() => setMoreOptionsAnchorEl(null)}
                >
                  <MenuItem onClick={() => setIsOpenDeleteLead(true)}>
                    <div className="flex items-center justify-between w-100">
                      <div className="flex items-center gap-2">
                        <FaTrash size={12} />
                        Удалить заявку
                      </div>
                    </div>
                  </MenuItem>
                </Menu>
              </div>
            </div>
            <div className="lead-funnel mt-4 flex gap-2">
              <Controller control={methods.control} name="pipelineID" render={({ field }) => (
                <FormControl>
                  <InputLabel>Воронка</InputLabel>
                  <Select style={{
                    width: 200,
                    maxWidth: 200,
                  }} label="Воронка" size="small" {...field}>
                    {pipelines?.map((pipeline: any) => (
                      <MenuItem key={pipeline.id} value={pipeline.id}>{pipeline.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )} />

              <div className="lead-status flex gap-2 items-center flex-1 w-full">
                {pipeline?.steps?.map((step: any) => (
                  <div key={step.id} className="flex-1">
                    <Button
                      size="small"
                      fullWidth
                      key={step.id}
                      variant={methods.watch('stepID') === step.id ? "contained" : "outlined"}
                      color={getStepColor(step)}
                      onClick={() => methods.setValue('stepID', step.id)}>
                      <span className="text-nowrap">{step.name}</span>
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            <div className="lead-body mt-6">
              <Tabs value={selectedTab} onChange={(e, value) => setSelectedTab(value)}>
                <Tab label="Основное" value="main" />
                {leadID && <Tab label="Задачи" value="tasks" />}
                {leadID && <Tab label="Документы" value="documents" />}
              </Tabs>
              <section className="mt-4">
                {selectedTab === 'main' && <LeadMainInfoV2
                  lead={lead}
                  deleteCustomerFromLead={deleteCustomerFromLead}
                  addCustomerToLead={addCustomerToLead}
                />
                }
                {selectedTab === 'tasks' && leadID && <LeadTasks leadID={leadID} />
                }
                {selectedTab === 'documents' && leadID && <LeadDocuments leadID={leadID} />
                }
              </section>
            </div>

          </section>
          {/* <pre>{JSON.stringify(methods.watch(), null, 2)}</pre> */}
        </form>
      </FormProvider>
      {/* Delete lead dialog */}
      <Dialog fullWidth maxWidth="sm" open={isOpenDeleteLead} onClose={() => { setIsOpenDeleteLead(false) }}>
        <DialogTitle>Удалить сделку</DialogTitle>
        <DialogContent>
          <div>Вы уверены, что хотите удалить эту сделку?</div>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="primary" onClick={onDeleteLead}>Удалить</Button>
          <Button variant="outlined" color="info" onClick={() => { setIsOpenDeleteLead(false) }}>Отмена</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};


interface LeadFormWrapperProps {
  leadID?: number
  onClose: (updatedLeadOrNewLead: { isNew: boolean, lead: Lead }) => void
  selectedPipelineID: string
}
const LeadFormWrapper = ({ leadID, onClose, selectedPipelineID }: LeadFormWrapperProps) => {
  const { data: lead } = useGetLead(leadID);
  const { data: pipelines } = useGetPipelines(localStorage.getItem('orgID') || '', 'leads');
  console.log("LeadFormWrapper.pipelines:", pipelines)

  console.log("LeadFormWrapper.lead:", lead)

  if (!pipelines) {
    return <div>Загрузка воронки...</div>
  }

  if (leadID) {
    if (!lead) {
      return <div>Загрузка сделки...</div>
    }
    return <LeadForm
      leadID={leadID}
      lead={lead}
      onClose={onClose}
      pipelines={pipelines}
      selectedPipelineID={selectedPipelineID}
    />
  }

  return (
    <LeadForm
      leadID={null}
      lead={null}
      onClose={onClose}
      pipelines={pipelines}
      selectedPipelineID={selectedPipelineID}
    />
  )
}

export default LeadFormWrapper;